import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles,
  TrendingUp,
  AlertCircle,
  Eye,
  Film,
  ArrowRight,
  Check,
  Sliders,
  BookOpen,
} from "lucide-react";
import ValuePropIllustration from "@/components/ValuePropIllustration";
import AavaaLogo from "@/components/AavaaLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CUSTOM_SIGNALS = [
  "Throwing stuff",
  "Smiling or crying",
  "Eating habits",
  "Sleep patterns",
  "Screen time",
  "Tantrums",
  "Social interaction",
  "Focus during activities",
  "Caregiver interaction",
  "Safety concerns",
  "Aggressive behavior",
  "Developmental milestones",
];

export default function LandingPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    child_age: "",
    insight: "",
    camera_status: "",
    question: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Scroll to waitlist if URL has #waitlist hash
  useEffect(() => {
    if (window.location.hash === "#waitlist") {
      setTimeout(() => {
        document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    }
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.email && !form.phone) {
      toast.error("Please share an email or a phone number so we can reach you.");
      return;
    }
    if (!form.insight.trim()) {
      toast.error("Please tell us what insight you're looking for about your child.");
      return;
    }
    if (!form.camera_status) {
      toast.error("Please let us know whether you have a camera at home.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, form);
      setSubmitted(true);
      toast.success("Thanks — we'll reach out soon to schedule your demo.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  const scrollToForm = () => {
    document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#2F3630]" data-testid="landing-page">
      {/* ====== Navbar ====== */}
      <nav className="sticky top-0 z-40 bg-[#FAFAF7]/85 backdrop-blur-xl border-b border-[#E2DFD8]/50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="nav-home-link">
            <AavaaLogo variant="wordmark" size={36} />
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/how-it-works"
              className="hidden sm:inline font-manrope text-sm text-[#6B7264] hover:text-[#2F3630] transition-colors"
              data-testid="nav-how-it-works"
            >
              How it works
            </Link>
            <Button
              data-testid="nav-request-demo-btn"
              onClick={scrollToForm}
              className="rounded-full bg-[#8E9B82] hover:bg-[#7A8A6E] text-white h-10 px-5 font-outfit font-medium text-sm"
            >
              Request demo
            </Button>
          </div>
        </div>
      </nav>

      {/* ====== Hero ====== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-20 -left-20 w-[340px] h-[340px] rounded-full bg-[#8E9B82]/10 blur-3xl" />
          <div className="absolute top-40 -right-20 w-[280px] h-[280px] rounded-full bg-[#D4A373]/15 blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-10 sm:pt-20 pb-12 sm:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-white border border-[#E2DFD8] rounded-full px-3.5 py-1.5 mb-6 shadow-[0_2px_10px_-4px_rgba(47,54,48,0.06)]">
              <Sparkles size={14} className="text-[#D4A373]" />
              <span className="font-manrope text-xs text-[#6B7264]">Early access — limited demo slots</span>
            </div>

            <h1 className="font-outfit font-medium text-[#2F3630] leading-[1.05] tracking-tight text-4xl sm:text-5xl lg:text-6xl mb-5">
              Know how your child's day <em className="italic font-light text-[#8E9B82]">really</em> went.
            </h1>

            <p className="font-manrope text-base sm:text-lg text-[#6B7264] leading-relaxed max-w-xl mb-7">
              Aavaa is a gentle AI companion that watches your existing home camera & gives you a summary of{" "}
              <button
                type="button"
                onClick={() => document.getElementById("customize")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="text-[#8E9B82] font-medium underline decoration-[#8E9B82]/40 decoration-dotted underline-offset-4 hover:decoration-[#8E9B82] hover:text-[#7A8A6E] transition-colors"
                data-testid="hero-behaviors-link"
              >
                the behaviors and signals you choose to track
              </button>
              , backed by real clips.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
              <Button
                data-testid="hero-request-demo-btn"
                onClick={scrollToForm}
                className="rounded-full bg-[#8E9B82] hover:bg-[#7A8A6E] text-white h-12 px-7 font-outfit font-medium text-base shadow-[0_6px_20px_-8px_rgba(142,155,130,0.6)] transition-all duration-300 hover:scale-[0.98] active:scale-95"
              >
                Request demo <ArrowRight size={18} className="ml-1.5" />
              </Button>
              <Link
                to="/how-it-works"
                className="flex items-center gap-1.5 text-sm font-manrope text-[#6B7264] hover:text-[#2F3630] transition-colors"
                data-testid="hero-how-it-works-link"
              >
                <BookOpen size={14} /> How Aavaa works
              </Link>
            </div>

            <p className="font-manrope text-xs text-[#6B7264]/80">
              Built first for parents of children with special needs.
            </p>
          </div>

          {/* Hero phone mock */}
          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <HeroPhoneMock />
          </div>
        </div>
      </section>

      {/* ====== How Aavaa works — visual process ====== */}
      <section className="bg-white/60 border-y border-[#E2DFD8]/50" id="how-it-works-inline">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="font-manrope text-xs tracking-[0.15em] text-[#D4A373] uppercase mb-3">How it works</p>
            <h2 className="font-outfit text-3xl sm:text-4xl font-medium tracking-tight leading-tight">
              A quiet loop that turns video into insight.
            </h2>
            <p className="font-manrope text-[#6B7264] mt-3">
              Your child plays. Your camera records. Aavaa analyzes.
            </p>
          </div>
          <ValuePropIllustration />
          <div className="text-center mt-6">
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-1.5 font-manrope text-sm text-[#8E9B82] hover:text-[#7A8A6E] font-medium"
              data-testid="process-read-article-link"
            >
              Read the full walkthrough <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ====== What Aavaa gives you ====== */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="max-w-xl mb-12">
          <p className="font-manrope text-xs tracking-[0.15em] text-[#D4A373] uppercase mb-3">What Aavaa gives you</p>
          <h2 className="font-outfit text-3xl sm:text-4xl font-medium tracking-tight leading-tight">
            Four things, every day.
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            {
              icon: Eye,
              title: "Daily behaviors",
              desc: "Was she cheerful? Did she eat well? Did she wear her glasses? Answered every evening, clearly.",
            },
            {
              icon: AlertCircle,
              title: "Early warning signs",
              desc: "Repeated throwing, prolonged crying, refusing to stand — flagged the moment they happen.",
            },
            {
              icon: TrendingUp,
              title: "Weekly & monthly trends",
              desc: "See engagement, mobility and independence shift week by week, so progress feels real.",
            },
            {
              icon: Film,
              title: "Clip-backed evidence",
              desc: "Every observation links to the exact clips it's based on. Nothing vague, nothing made up.",
            },
          ].map((s, i) => (
            <div
              key={i}
              className="bg-white rounded-[24px] p-6 border border-[#E2DFD8]/60 hover:border-[#8E9B82]/40 hover:shadow-[0_8px_30px_-12px_rgba(47,54,48,0.08)] transition-all duration-300 animate-fade-up"
              style={{ animationDelay: `${i * 0.06}s` }}
            >
              <div className="w-10 h-10 rounded-xl bg-[#8E9B82]/10 flex items-center justify-center mb-4">
                <s.icon size={18} className="text-[#8E9B82]" strokeWidth={1.75} aria-hidden="true" />
              </div>
              <h3 className="font-outfit text-lg font-medium text-[#2F3630] mb-1.5 tracking-tight">{s.title}</h3>
              <p className="font-manrope text-sm text-[#6B7264] leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ====== Customization spotlight ====== */}
      <section id="customize" className="bg-gradient-to-br from-[#F3EFE6] to-[#FAFAF7] border-y border-[#E2DFD8]/50 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 bg-white rounded-full px-3.5 py-1.5 border border-[#E2DFD8] mb-5">
                <Sliders size={12} className="text-[#D4A373]" />
                <span className="font-manrope text-xs text-[#6B7264] uppercase tracking-[0.1em]">You're in control</span>
              </div>
              <h2 className="font-outfit text-3xl sm:text-4xl font-medium tracking-tight leading-tight mb-4">
                Customize the exact behaviors and signals Aavaa should look for.
              </h2>
              <p className="font-manrope text-[#6B7264] leading-relaxed mb-5">
                Every child is different. Every parent's worries are different. During onboarding, pick exactly what Aavaa should track — and add your own, in your own words.
              </p>
              <p className="font-manrope text-sm text-[#6B7264] leading-relaxed">
                Pop in things like <em>"Did she try to say mama today?"</em> or <em>"Did he bang his head on the crib?"</em> — Aavaa will watch for it.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="flex flex-wrap gap-2.5">
                {CUSTOM_SIGNALS.map((s, i) => (
                  <span
                    key={i}
                    className="bg-white border border-[#E2DFD8] rounded-full px-4 py-2 text-sm font-manrope text-[#2F3630] shadow-[0_2px_10px_-4px_rgba(47,54,48,0.05)] hover:border-[#8E9B82]/50 transition-colors animate-fade-up"
                    style={{ animationDelay: `${i * 0.04}s` }}
                  >
                    {s}
                  </span>
                ))}
                <span className="bg-[#8E9B82]/10 border border-[#8E9B82]/30 rounded-full px-4 py-2 text-sm font-manrope text-[#8E9B82] font-medium">
                  + your own custom signal
                </span>
              </div>
              <Link
                to="/how-it-works"
                className="inline-flex items-center gap-1.5 font-manrope text-sm text-[#8E9B82] hover:text-[#7A8A6E] font-medium mt-6"
                data-testid="customization-learn-more-link"
              >
                See a full list of what Aavaa can monitor <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ====== Waitlist / Request demo form ====== */}
      <section id="waitlist" className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-white border border-[#E2DFD8] rounded-full px-3.5 py-1.5 mb-5">
            <div className="w-1.5 h-1.5 rounded-full bg-[#8E9B82] animate-pulse" />
            <span className="font-manrope text-xs text-[#6B7264]">Personal demos — 15 minutes each</span>
          </div>
          <h2 className="font-outfit text-3xl sm:text-4xl font-medium tracking-tight leading-tight mb-3">
            Request your demo.
          </h2>
          <p className="font-manrope text-[#6B7264] max-w-xl mx-auto">
            Tell us a little about your child, and we'll reach out personally to walk you through Aavaa — tailored to the insights <strong>you</strong> care most about.
          </p>
        </div>

        {submitted ? (
          <div className="bg-white rounded-[28px] p-8 sm:p-10 border border-[#8E9B82]/30 shadow-[0_8px_30px_-12px_rgba(142,155,130,0.2)] text-center animate-fade-up" data-testid="waitlist-success">
            <div className="w-14 h-14 rounded-full bg-[#8E9B82]/15 flex items-center justify-center mx-auto mb-4">
              <Check size={26} className="text-[#8E9B82]" strokeWidth={2} />
            </div>
            <h3 className="font-outfit text-2xl font-medium mb-2">Thanks — we'll be in touch.</h3>
            <p className="font-manrope text-[#6B7264] leading-relaxed max-w-md mx-auto">
              Your demo request is in. Expect an email or a call from us within the next day or two to schedule a personal walkthrough.
            </p>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="bg-white rounded-[28px] p-6 sm:p-8 border border-[#E2DFD8]/60 shadow-[0_8px_30px_-12px_rgba(47,54,48,0.08)] space-y-5"
            data-testid="waitlist-form"
          >
            <div>
              <label htmlFor="wf-name" className="text-xs font-manrope text-[#6B7264] tracking-wide uppercase mb-1.5 block">Your name</label>
              <Input
                id="wf-name"
                data-testid="waitlist-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Priya"
                className="aavaa-input rounded-xl border-[#E2DFD8] h-12 focus:ring-2 focus:ring-[#8E9B82]/20 focus:border-[#8E9B82]"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="wf-email" className="text-xs font-manrope text-[#6B7264] tracking-wide uppercase mb-1.5 block">Email</label>
                <Input
                  id="wf-email"
                  data-testid="waitlist-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@email.com"
                  className="aavaa-input rounded-xl border-[#E2DFD8] h-12 focus:ring-2 focus:ring-[#8E9B82]/20 focus:border-[#8E9B82]"
                />
              </div>
              <div>
                <label htmlFor="wf-phone" className="text-xs font-manrope text-[#6B7264] tracking-wide uppercase mb-1.5 block">Phone</label>
                <Input
                  id="wf-phone"
                  data-testid="waitlist-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+91 9XXXX XXXXX"
                  className="aavaa-input rounded-xl border-[#E2DFD8] h-12 focus:ring-2 focus:ring-[#8E9B82]/20 focus:border-[#8E9B82]"
                />
              </div>
            </div>

            <div>
              <label htmlFor="wf-age" className="text-xs font-manrope text-[#6B7264] tracking-wide uppercase mb-1.5 block">Your child's age (optional)</label>
              <Input
                id="wf-age"
                data-testid="waitlist-child-age"
                value={form.child_age}
                onChange={(e) => setForm({ ...form, child_age: e.target.value })}
                placeholder="e.g. 2 years, 3 months"
                className="aavaa-input rounded-xl border-[#E2DFD8] h-12 focus:ring-2 focus:ring-[#8E9B82]/20 focus:border-[#8E9B82]"
              />
            </div>

            <div>
              <label htmlFor="wf-insight" className="text-xs font-manrope text-[#6B7264] tracking-wide uppercase mb-1.5 block">
                What specific insight do you generally look for, about your child's day? <span className="text-[#D4A373] normal-case tracking-normal">(required)</span>
              </label>
              <Textarea
                id="wf-insight"
                data-testid="waitlist-insight"
                value={form.insight}
                onChange={(e) => setForm({ ...form, insight: e.target.value })}
                placeholder="Share what you most want to know about your child's day…"
                required
                rows={3}
                className="aavaa-input rounded-xl border-[#E2DFD8] focus:ring-2 focus:ring-[#8E9B82]/20 focus:border-[#8E9B82] text-sm"
              />
              <p className="font-manrope text-xs text-[#6B7264]/80 mt-1.5 leading-relaxed">
                <em>Example: I want to know how often my child throws their glasses, how long they stay focused during play, or whether they seem happy, upset, or tired during the day.</em>
              </p>
            </div>

            <div>
              <p className="text-xs font-manrope text-[#6B7264] tracking-wide uppercase mb-2">
                Do you already have a child monitoring camera at home? <span className="text-[#D4A373] normal-case tracking-normal">(required)</span>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  { v: "yes", l: "Yes" },
                  { v: "no", l: "No" },
                  { v: "planning", l: "Planning to buy one soon" },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    data-testid={`waitlist-camera-${o.v}`}
                    onClick={() => setForm({ ...form, camera_status: o.v })}
                    className={`rounded-xl border px-4 py-3 font-manrope text-sm transition-all duration-200 ${
                      form.camera_status === o.v
                        ? "bg-[#8E9B82] border-[#8E9B82] text-white shadow-[0_4px_14px_-4px_rgba(142,155,130,0.5)]"
                        : "bg-white border-[#E2DFD8] text-[#2F3630] hover:border-[#8E9B82]/50"
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="wf-question" className="text-xs font-manrope text-[#6B7264] tracking-wide uppercase mb-1.5 block">
                Do you have any questions for us? <span className="normal-case tracking-normal text-[#6B7264]/60">(optional)</span>
              </label>
              <Textarea
                id="wf-question"
                data-testid="waitlist-question"
                value={form.question}
                onChange={(e) => setForm({ ...form, question: e.target.value })}
                placeholder="Anything you're curious about, concerned about, or want us to address in the demo."
                rows={2}
                className="aavaa-input rounded-xl border-[#E2DFD8] focus:ring-2 focus:ring-[#8E9B82]/20 focus:border-[#8E9B82] text-sm"
              />
            </div>

            <p className="font-manrope text-xs text-[#6B7264]/80">
              Please share at least one contact — email or phone. We'll never spam you.
            </p>

            <Button
              data-testid="waitlist-submit"
              type="submit"
              disabled={submitting}
              className="w-full rounded-full bg-[#8E9B82] hover:bg-[#7A8A6E] text-white h-12 font-outfit font-medium text-base transition-all duration-300 hover:scale-[0.98] active:scale-95"
            >
              {submitting ? "Sending your request..." : "Request demo"}
              {!submitting && <ArrowRight size={18} className="ml-1.5" />}
            </Button>
          </form>
        )}
      </section>

      {/* ====== Footer ====== */}
      <footer className="border-t border-[#E2DFD8]/60">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center" data-testid="footer-home-link">
            <AavaaLogo variant="wordmark" size={30} />
          </Link>
          <div className="flex items-center gap-5 text-xs font-manrope text-[#6B7264]">
            <Link to="/how-it-works" className="hover:text-[#2F3630] transition-colors" data-testid="footer-how-it-works">How it works</Link>
            <Link to="/privacy" className="hover:text-[#2F3630] transition-colors" data-testid="footer-privacy-link">Privacy</Link>
            <button onClick={scrollToForm} className="hover:text-[#2F3630] transition-colors" data-testid="footer-demo-cta">Request demo</button>
            <span className="opacity-60">© 2026 Aavaa</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ================= Hero phone mock ================= */
function HeroPhoneMock() {
  const observations = [
    { t: "Was she cheerful today?", v: "Yes, mostly", clips: 4, color: "#8E9B82", tones: ["#8E9B82", "#A7B399", "#D4A373"] },
    { t: "Did she wear her glasses?", v: "2 removals noted", clips: 3, color: "#D4A373", tones: ["#D4A373", "#E7C19A", "#8E9B82"] },
    { t: "Did she follow instructions?", v: "4 of 6 times", clips: 6, color: "#8E9B82", tones: ["#8E9B82", "#D4A373", "#A7B399"] },
  ];
  return (
    <div className="relative mx-auto w-full max-w-[320px]" data-testid="hero-phone-mock">
      <div className="absolute -inset-6 rounded-[56px] bg-gradient-to-br from-[#8E9B82]/20 via-transparent to-[#D4A373]/25 blur-2xl -z-0" aria-hidden="true" />

      <div className="relative bg-[#1F2420] rounded-[42px] p-3 shadow-[0_30px_60px_-20px_rgba(47,54,48,0.4)]">
        <div className="bg-[#FAFAF7] rounded-[32px] overflow-hidden">
          <div className="flex items-center justify-between px-5 pt-4 pb-2 text-[10px] font-manrope text-[#6B7264]">
            <span>9:41</span>
            <div className="flex items-center gap-1 opacity-60">
              <span>●●●</span>
              <span>Wi-Fi</span>
            </div>
          </div>

          <div className="px-5 pb-6 pt-1">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-1.5 h-5 rounded-full bg-[#D4A373]" />
              <p className="font-outfit text-lg font-medium text-[#2F3630]">Aavyaa's Day</p>
            </div>
            <p className="font-manrope text-[10px] text-[#6B7264] mb-4 ml-3">14 cheerful moments · 3 things to know</p>

            <div className="bg-white rounded-2xl p-3.5 border border-[#E2DFD8]/60 mb-3">
              <p className="font-outfit text-sm font-medium text-[#2F3630] mb-1">Overview</p>
              <p className="font-manrope text-[11px] text-[#6B7264] leading-relaxed">
                Aavyaa was mostly cheerful today — giggling during music time and engaged through puzzle play. Glasses came off twice during meals.
              </p>
            </div>

            {observations.map((o, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-3 border border-[#E2DFD8]/60 mb-2 animate-fade-up"
                style={{ animationDelay: `${0.4 + i * 0.1}s` }}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-manrope text-[10px] text-[#6B7264]">{o.t}</p>
                    <p className="font-outfit text-xs font-medium text-[#2F3630] mt-0.5">{o.v}</p>
                  </div>
                  <div className="w-1.5 h-9 rounded-full flex-shrink-0" style={{ background: o.color }} />
                </div>
                {/* Mini sample clip thumbnails */}
                <div className="flex gap-1.5">
                  {o.tones.map((t, j) => (
                    <div
                      key={j}
                      className="w-12 h-8 rounded-md relative overflow-hidden"
                      style={{
                        background: `linear-gradient(135deg, ${t}CC, ${t}88)`,
                      }}
                      aria-label={`Sample clip thumbnail ${j + 1}`}
                    >
                      {/* faux depth lines to feel like a frame */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-3 h-3 rounded-full bg-white/70 flex items-center justify-center">
                          <span className="block w-0 h-0 border-l-[3px] border-l-[#2F3630] border-y-[2.5px] border-y-transparent ml-[1px]" />
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="w-12 h-8 rounded-md bg-[#F7F5F0] border border-[#E2DFD8] flex items-center justify-center">
                    <span className="font-manrope text-[9px] text-[#6B7264] font-medium">+{Math.max(0, o.clips - 3)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute -top-3 -right-3 bg-white rounded-full border border-[#E2DFD8] px-3 py-1.5 flex items-center gap-1.5 shadow-md">
        <Sparkles size={12} className="text-[#D4A373]" />
        <span className="font-manrope text-[10px] text-[#6B7264] font-medium">Today's summary</span>
      </div>
    </div>
  );
}
